import { useLayoutEffect, useRef, useState } from "react";

/**
 * Cross-route composer morph.
 *
 * The new-thread page (`/new`) and a draft thread (`/draft/$draftId`) are
 * different routes rendering different components, so the composer the user
 * just typed into is unmounted and a second one is mounted in its place. A
 * same-node FLIP is therefore not available here.
 *
 * Tagging both composers with the same `view-transition-name` hands that job to
 * the browser: it snapshots the outgoing composer (centered hero) and the
 * incoming one (docked at the bottom) and interpolates between the two boxes.
 * The user sees the box they were typing in travel to its resting place instead
 * of the page swapping under them.
 *
 * The name must be unique per document while a transition runs — two live
 * elements sharing it abort the transition — which is why exactly one composer
 * may carry it at a time. `/new` and `/draft` never render together, so the
 * constraint holds by routing.
 */
export const COMPOSER_VIEW_TRANSITION_NAME = "forge-composer";

/**
 * Style applied to the composer wrapper on each participating route.
 *
 * Frozen and shared rather than built per render: it is the same object every
 * time, and a stable identity keeps it out of React's style-diffing churn.
 */
export const COMPOSER_VIEW_TRANSITION_STYLE: Readonly<{ viewTransitionName: string }> =
  Object.freeze({
    viewTransitionName: COMPOSER_VIEW_TRANSITION_NAME,
  });

/**
 * Single owner of the shared name.
 *
 * Three routes render a composer that wants the morph: `/new` through
 * NewThreadComposer, and both `/draft/$draftId` and the thread route through
 * ChatComposerDock. A router transition renders the incoming route before the
 * outgoing one unmounts, so without arbitration two live elements carry the
 * name and the browser aborts the transition with an InvalidStateError.
 *
 * Ownership goes to whoever holds it, and transfers on release rather than
 * being abandoned, so the surviving composer is named once the other unmounts.
 */
let viewTransitionNameOwner: object | null = null;
const viewTransitionNameWaiters = new Set<() => void>();

function acquireComposerViewTransitionName(owner: object): boolean {
  if (viewTransitionNameOwner === null) {
    viewTransitionNameOwner = owner;
    return true;
  }
  return viewTransitionNameOwner === owner;
}

function releaseComposerViewTransitionName(owner: object): void {
  if (viewTransitionNameOwner !== owner) {
    return;
  }
  viewTransitionNameOwner = null;
  // Copied first: a waiter that wins the claim can unmount and remove itself
  // from this set while we are still iterating it. The copy is the point, so
  // the rule is wrong here rather than the code.
  // eslint-disable-next-line unicorn/no-useless-spread
  for (const notify of [...viewTransitionNameWaiters]) {
    notify();
  }
}

/**
 * Style for a composer that may participate in the cross-route morph.
 *
 * Returns undefined for every composer but the current owner, which is what
 * keeps the document to exactly one element carrying the name.
 */
export function useComposerViewTransitionStyle():
  | Readonly<{ viewTransitionName: string }>
  | undefined {
  const ownerRef = useRef<object | null>(null);
  ownerRef.current ??= {};
  const owner = ownerRef.current;
  const [owns, setOwns] = useState(false);

  useLayoutEffect(() => {
    const claim = () => {
      setOwns(acquireComposerViewTransitionName(owner));
    };
    claim();
    viewTransitionNameWaiters.add(claim);
    return () => {
      viewTransitionNameWaiters.delete(claim);
      releaseComposerViewTransitionName(owner);
    };
  }, [owner]);

  return owns ? COMPOSER_VIEW_TRANSITION_STYLE : undefined;
}

/** Test seam: drop any claim left by a previous test's composer. */
export function resetComposerViewTransitionNameForTests(): void {
  viewTransitionNameOwner = null;
  viewTransitionNameWaiters.clear();
}

/**
 * Whether this client should animate the morph at all.
 *
 * Reduced-motion users get the plain route swap. Browsers without the API fall
 * back the same way, and TanStack Router degrades `viewTransition: true` to a
 * normal transition on its own, so callers do not need a second guard.
 */
export function prefersComposerViewTransition(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }
  if (!("startViewTransition" in document)) {
    return false;
  }
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches !== true;
}
